import { Music } from 'lucide-react'
import { useEffect, useState } from 'react'

import { cn } from '@/lib/cn'

export interface PlaylistMosaicProps {
  /** Artwork for the first few tracks, in playlist order. Only four are used. */
  coverUrls: string[]
  /** Width comes from here; the box is always square. */
  className?: string
}

function Tile({ src }: { src: string }) {
  const [failed, setFailed] = useState(false)

  // A recycled tile can point at different artwork; a previous failure must not
  // stick to the new URL.
  useEffect(() => {
    setFailed(false)
  }, [src])

  if (failed) return <span className="block h-full w-full bg-surface-hover" />

  return (
    <img
      src={src}
      alt=""
      loading="lazy"
      decoding="async"
      draggable={false}
      onError={() => setFailed(true)}
      className="h-full w-full object-cover"
    />
  )
}

/**
 * The cover tile for a playlist: a 2x2 grid once there are four distinct covers,
 * and the first cover full-bleed before that. A half-filled grid reads as a
 * broken image rather than as a deliberate collage.
 *
 * Renders a `<span>`, so it is legal inside a link or a button.
 */
export function PlaylistMosaic({ coverUrls, className }: PlaylistMosaicProps) {
  const covers = coverUrls.filter((url) => Boolean(url)).slice(0, 4)

  return (
    <span
      className={cn(
        'relative block aspect-square overflow-hidden rounded-card bg-surface-hover',
        className,
      )}
    >
      {covers.length === 0 ? (
        <span className="flex h-full w-full items-center justify-center" aria-hidden="true">
          <Music className="h-1/4 w-1/4 text-text-subtle" />
        </span>
      ) : covers.length < 4 ? (
        <Tile src={covers[0]} />
      ) : (
        <span className="grid h-full w-full grid-cols-2 grid-rows-2">
          {covers.map((url, position) => (
            <span key={`${position}-${url}`} className="block overflow-hidden">
              <Tile src={url} />
            </span>
          ))}
        </span>
      )}
    </span>
  )
}
